import { useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from 'api/base';
import type { PausedConsultationsResponse } from 'types/pausedConsultation';
import { pageFromNextLink } from 'utils/pagination';

export const PAUSED_CONSULTATIONS_QUERY_KEY = ['pausedConsultations'] as const;

export const pausedConsultationsQueryKey = (params?: { medatixxPatientId?: string }) =>
  [...PAUSED_CONSULTATIONS_QUERY_KEY, params] as const;

export function usePausedConsultations(
  params?: { medatixxPatientId?: string },
  options?: { enabled?: boolean }
) {
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: pausedConsultationsQueryKey(params),
      queryFn: ({ pageParam }) => {
        const fetchParams: Record<string, unknown> = { ...params };
        const page = pageFromNextLink(pageParam);
        if (page) fetchParams.page = page;
        return get(
          'pausedConversations',
          undefined,
          fetchParams
        ) as Promise<PausedConsultationsResponse>;
      },
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => lastPage.next || null,
      enabled: options?.enabled ?? true,
    });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError)
      void fetchNextPage({ cancelRefetch: false });
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const consultations = useMemo(
    () => data?.pages.flatMap((page) => page.results) ?? [],
    [data?.pages]
  );

  return { consultations, isLoading, isError };
}
